
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
  AlertDialogTitle, AlertDialogTrigger 
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Trash2, AlertTriangle } from 'lucide-react';

const DeleteAccount: React.FC = () => {
  const { user, signOut } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  const handleDeleteAccount = async () => {
    if (!user || !password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    if (confirmText !== 'DELETE') {
      toast.error('Please type "DELETE" to confirm');
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        toast.error('Session expired. Please log in again.');
        return;
      }

      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: { password },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        console.error('Account deletion error:', error);
        toast.error(error.message || 'Failed to delete account');
        return;
      }

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      toast.success('Your account has been permanently deleted');
      await signOut();
      setTimeout(() => { window.location.href = '/'; }, 1000);

    } catch (error) {
      console.error('Unexpected error during account deletion:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPassword('');
    setConfirmText('');
    setShowPasswordInput(false);
  };

  return (
    <div className="space-y-6">
      <div className="p-6 border border-red-200 rounded-lg bg-red-50 dark:bg-red-950 dark:border-red-800">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 dark:bg-red-900 rounded-full">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">Delete Account</h3>
            <p className="text-sm text-red-600 dark:text-red-400">Permanently delete your account and all associated data</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-900 p-4 rounded-md border border-red-200 dark:border-red-800">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">What will be deleted:</h4>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li>• Your profile and account information</li>
              <li>• All your task lists and tasks</li>
              <li>• Your focus session history</li>
              <li>• All settings and preferences</li>
            </ul>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-950 p-4 rounded-md border border-yellow-200 dark:border-yellow-800">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Warning:</strong> This action cannot be undone. Once deleted, your account and all data will be permanently removed from our servers.
            </p>
          </div>

          <AlertDialog onOpenChange={resetForm}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="w-full" onClick={() => setShowPasswordInput(true)}>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete My Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-md">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  Confirm Account Deletion
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-4">
                  <p>This will permanently delete your account and all associated data. This action cannot be undone.</p>
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="delete-password" className="text-sm font-medium">Enter your password to confirm:</Label>
                      <Input id="delete-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Your current password" className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="delete-confirm" className="text-sm font-medium">Type "DELETE" to confirm:</Label>
                      <Input id="delete-confirm" type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type DELETE" className="mt-1" />
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel onClick={resetForm}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteAccount} disabled={loading || !password.trim() || confirmText !== 'DELETE'} className="bg-red-600 hover:bg-red-700 focus:ring-red-600">
                  {loading ? 'Deleting...' : 'Delete Account'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
